<template>
  <div v-if="showLtcsRequest" class="sidebar-bottom">
    <button
      type="button"
      class="hr-ltcs-request-btn"
      :class="btnStatus ? `hr-ltcs-request-btn--${btnStatus}` : undefined"
      @click="showModal = true"
    >
      <div class="hr-ltcs-request-btn__main">
        <v-icon size="14">mdi-cloud-download-outline</v-icon>
        <span>롱텀케어 자료요청</span>
      </div>
      <div class="hr-ltcs-request-btn__status hr-ltcs-request-btn__status--multi">
        <span
          v-for="(item, idx) in typeStatuses"
          :key="item.type"
          class="status-item"
        >
          <span v-if="idx > 0" class="status-sep">/</span>
          <span
            class="status-dot"
            :class="getStatusDotClass(item.status)"
          ></span>
          <span class="status-label">{{ item.label }}</span>
        </span>
      </div>
    </button>
  </div>

  <LtcsHrRequestModal
    v-model="showModal"
    :member-code="memberCode"
    :biz-gubun="bizGubun"
    :year-month="yearMonth"
    :allowed-types="allowedTypes"
    @updated="onModalUpdated"
  />
</template>

<script setup lang="ts">
import { ref, computed, watch, onUnmounted } from 'vue';
import LtcsHrRequestModal from '@/components/member/request/LtcsHrRequestModal.vue';
import {
  HR_LTCS_REQUEST_TYPES,
  WAGE_LTCS_REQUEST_TYPES,
  fetchLtcsTypeStatuses,
  aggregateLtcsBtnStatus,
  type LtcsRequestBtnStatus,
  type LtcsTypeStatusItem,
} from '@/composables/useLtcsMacroRequest';
import { isLtcsMacroBizGubun } from '@/constants/ltcsMacro';

const props = withDefaults(
  defineProps<{
    memberCode: string;
    bizGubun: string;
    yearMonth: string;
    variant?: 'hr' | 'wage';
  }>(),
  {
    variant: 'hr',
  }
);

const emit = defineEmits<{
  (e: 'updated'): void;
  (e: 'ready'): void;
}>();

const showModal = ref(false);
const typeStatuses = ref<LtcsTypeStatusItem[]>([]);
const prevStatusByType = ref<Record<string, LtcsRequestBtnStatus>>({});
let statusPolling: ReturnType<typeof setInterval> | null = null;

const allowedTypes = computed(() =>
  props.variant === 'wage' ? WAGE_LTCS_REQUEST_TYPES : HR_LTCS_REQUEST_TYPES
);

const showLtcsRequest = computed(() => isLtcsMacroBizGubun(props.bizGubun));

const btnStatus = computed(() => aggregateLtcsBtnStatus(typeStatuses.value));

const getStatusDotClass = (status: LtcsRequestBtnStatus) => {
  if (status === 'ready') return ['status-dot--ready'];
  if (status === 'processing') return ['status-dot--processing', 'status-dot--pulse'];
  return ['status-dot--bad'];
};

const emitReadyIfNeeded = (prev: Record<string, LtcsRequestBtnStatus>) => {
  const anyNewReady = typeStatuses.value.some(
    (item) =>
      item.status === 'ready' &&
      prev[item.type] !== undefined &&
      prev[item.type] !== 'ready'
  );
  if (anyNewReady) {
    emit('ready');
  }
};

const loadBtnStatus = async () => {
  const prev = { ...prevStatusByType.value };

  typeStatuses.value = await fetchLtcsTypeStatuses(
    props.memberCode,
    props.bizGubun,
    props.yearMonth,
    allowedTypes.value
  );

  const nextMap: Record<string, LtcsRequestBtnStatus> = {};
  typeStatuses.value.forEach((item) => {
    nextMap[item.type] = item.status;
  });
  prevStatusByType.value = nextMap;
  emitReadyIfNeeded(prev);
};

const stopStatusPolling = () => {
  if (statusPolling) {
    clearInterval(statusPolling);
    statusPolling = null;
  }
};

const startStatusPolling = () => {
  if (statusPolling) return;
  statusPolling = setInterval(async () => {
    await loadBtnStatus();
    if (btnStatus.value !== 'processing') {
      stopStatusPolling();
    }
  }, 4000);
};

const onModalUpdated = async () => {
  await loadBtnStatus();
  emit('updated');

  if (btnStatus.value === 'processing') {
    startStatusPolling();
  }
};

watch(
  () => [props.memberCode, props.bizGubun, props.yearMonth, props.variant] as const,
  () => {
    loadBtnStatus();
  },
  { immediate: true }
);

watch(btnStatus, (status) => {
  if (status === 'processing') {
    startStatusPolling();
  }
});

onUnmounted(() => {
  stopStatusPolling();
});
</script>
